// Gaps API Hooks - React Query hooks for gap detection operations
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import apiClient from '../../services/unifiedApiClient';

/**
 * Fetch a summary of gaps across all ideas for the current user.
 * Returns { total, needs_attention } counts.
 */
export const useGapsSummary = (options = {}) => {
  return useQuery({
    queryKey: ['gaps', 'summary'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/ideas/gaps/summary');
        return response;
      } catch (err) {
        // Endpoint may not exist yet — return empty summary gracefully
        return { total: 0, needs_attention: 0 };
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Fetch gaps for a specific idea.
 */
export const useIdeaGaps = (ideaId, options = {}) => {
  return useQuery({
    queryKey: ['gaps', 'idea', ideaId],
    queryFn: async () => {
      if (!ideaId) return { gaps: [], total: 0 };
      try {
        const response = await apiClient.get(`/ideas/${ideaId}/gaps`);
        if (Array.isArray(response)) return { gaps: response, total: response.length };
        return response;
      } catch (err) {
        return { gaps: [], total: 0 };
      }
    },
    enabled: !!ideaId,
    ...options,
  });
};

// Alias matching the feature-branch API used in IdeaView
export const useGaps = useIdeaGaps;

/**
 * Trigger gap analysis on demand for an idea.
 * Returns immediately (202); polls until results arrive.
 * isAnalyzing stays true from the moment "Run" is clicked until fresh gaps land.
 */
export const useRunGapAnalysis = (ideaId) => {
  const queryClient = useQueryClient();
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const poll = useCallback((attempt = 0, triggeredAt = Date.now()) => {
    queryClient.invalidateQueries({ queryKey: ['gaps', 'idea', ideaId] }).then(() => {
      const state = queryClient.getQueryState(['gaps', 'idea', ideaId]);
      const dataUpdatedAt = state?.dataUpdatedAt ?? 0;
      if (dataUpdatedAt > triggeredAt) {
        // Fresh data arrived — stop spinning
        setIsAnalyzing(false);
        queryClient.invalidateQueries({ queryKey: ['gaps', 'summary'] });
      } else if (attempt < 10) {
        // Keep polling for up to ~30s
        setTimeout(() => poll(attempt + 1, triggeredAt), 3000);
      } else {
        setIsAnalyzing(false);
      }
    });
  }, [queryClient, ideaId]);

  const mutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(`/ideas/${ideaId}/gaps/analyze`);
    },
    onMutate: () => {
      setIsAnalyzing(true);
    },
    onSuccess: () => {
      const triggeredAt = Date.now();
      // Start polling — first check after 3s
      setTimeout(() => poll(0, triggeredAt), 3000);
    },
    onError: () => {
      setIsAnalyzing(false);
    },
  });

  return { ...mutation, isAnalyzing };
};

/**
 * Preview what spec changes would resolve a gap — runs the LLM but does NOT
 * write anything. Returns { reasoning, changes }.
 */
export const usePreviewGap = (ideaId) => {
  return useMutation({
    mutationFn: async ({ gapId, answer }) => {
      return await apiClient.post(`/ideas/${ideaId}/gaps/${gapId}/resolve/preview`, { answer });
    },
  });
};

/**
 * Resolve a gap by providing an answer — LLM applies changes to the spec,
 * then the gap is deleted.
 */
export const useResolveGap = (ideaId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ gapId, answer, confirmed_changes }) => {
      const body = { answer };
      if (confirmed_changes?.length) body.confirmed_changes = confirmed_changes;
      return await apiClient.post(`/ideas/${ideaId}/gaps/${gapId}/resolve`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gaps', 'idea', ideaId] });
      queryClient.invalidateQueries({ queryKey: ['gaps', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['idea', ideaId] });
    },
  });
};

/**
 * Dismiss (delete) a single gap for an idea.
 */
export const useDismissGap = (ideaId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (gapId) => {
      await apiClient.delete(`/ideas/${ideaId}/gaps/${gapId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gaps', 'idea', ideaId] });
      queryClient.invalidateQueries({ queryKey: ['gaps', 'summary'] });
    },
  });
};
